import React from 'react';
import PropTypes from 'prop-types';
import dayjs from 'dayjs';
import { MdPublic, MdDownload, MdCheckCircle, MdErrorOutline, MdHourglassTop } from 'react-icons/md';
import { Button } from 'components/common';

const STALE_AFTER_DAYS = 45;

/**
 * State of the IP geolocation database, and the button that installs it.
 *
 * Exists so nobody has to SSH into a server to make the map work. Without it the
 * only way to install the database was `rake geo:update`, which meant the
 * feature quietly stayed off on any server where nobody thought to run it — and
 * "no map anywhere" looks identical to "everyone happens to be on the LAN".
 */
const GeoDatabaseCard = ({ geo, onUpdate }) => {
  // null means the status has not loaded yet; rendering "not installed" during
  // that moment would be a lie that flashes on every page load.
  if (!geo || geo.available === null || geo.available === undefined) return null;

  const updatedAt = geo.updated_at ? dayjs(geo.updated_at) : null;
  // Both providers republish monthly, so a file older than this is quietly
  // returning stale locations rather than failing.
  const stale = updatedAt && dayjs().diff(updatedAt, 'days') > STALE_AFTER_DAYS;

  const tone = geo.running
    ? { bg: 'tw-bg-blue-50', border: 'tw-border-blue-200', icon: MdHourglassTop, color: 'tw-text-blue-600' }
    : geo.available && !stale
      ? { bg: 'tw-bg-emerald-50/60', border: 'tw-border-emerald-200', icon: MdCheckCircle, color: 'tw-text-emerald-600' }
      : { bg: 'tw-bg-amber-50', border: 'tw-border-amber-200', icon: MdErrorOutline, color: 'tw-text-amber-600' };

  const Icon = tone.icon;

  // When the online lookup answers first, this card describes the FALLBACK —
  // the local file only matters on the days ipwho.is does not answer. Saying so
  // stops two misreadings: "geolocation is broken" when only the fallback file
  // is missing, and "this card is obsolete, delete it" when it is not.
  const remote = Boolean(geo.remote_enabled);

  const headline = () => {
    if (geo.running) return 'Downloading the geolocation database…';
    if (!geo.available) {
      return remote
        ? 'The offline fallback database is not installed'
        : 'IP locations are unavailable on this server';
    }
    if (stale) return 'The geolocation database is out of date';
    return remote ? 'Offline fallback database is ready' : 'IP geolocation is active';
  };

  const detail = () => {
    if (geo.running) return 'This takes a minute or two. Reload the page to see the result.';
    if (!geo.available) {
      return remote
        ? 'Lookups come from ipwho.is online. Without this file there is no answer on the days that service is unreachable.'
        : 'The database is not installed, so sign-ins cannot be placed on a map. Everything else on this page works without it.';
    }

    const age = updatedAt ? `updated ${updatedAt.fromNow()}` : 'installation date unknown';
    const size = geo.size_mb ? `${geo.size_mb} MB` : null;
    const parts = [size, age, geo.last_provider].filter(Boolean).join(' · ');

    return stale
      ? `${parts}. Both providers republish monthly — refresh it to keep locations accurate.`
      : parts;
  };

  return (
    <div className={`tw-flex tw-items-start tw-gap-3 tw-mb-3 tw-px-4 tw-py-3 tw-rounded tw-border tw-border-solid ${tone.bg} ${tone.border}`}>
      <MdPublic size={18} className={`${tone.color} tw-shrink-0 tw-mt-0.5`} />

      <div className="tw-min-w-0 tw-flex-1">
        <p className="tw-text-sm tw-font-semibold tw-text-slate-700 tw-m-0 tw-flex tw-items-center tw-gap-1.5">
          <Icon size={14} className={tone.color} />
          {headline()}
        </p>
        <p className="tw-text-xs tw-text-slate-600 tw-m-0 tw-mt-0.5">{detail()}</p>

        {/* The outcome of the previous attempt, so a failure is not silent. */}
        {!geo.running && geo.last_state === 'failed' && geo.last_message && (
          <p className="tw-text-xs tw-text-red-600 tw-m-0 tw-mt-1">
            Last attempt failed: {geo.last_message}
          </p>
        )}
      </div>

      {/* Admin-only, and the server enforces that too — this just avoids
          offering an action that would be refused. */}
      {geo.can_update && (
        <Button size="sm" variant="secondary" onClick={onUpdate} disabled={geo.running}>
          <MdDownload size={14} /> {geo.available ? 'Update now' : 'Download now'}
        </Button>
      )}
    </div>
  );
};

GeoDatabaseCard.propTypes = {
  geo: PropTypes.shape({
    available: PropTypes.bool,
    remote_enabled: PropTypes.bool,
    running: PropTypes.bool,
    updated_at: PropTypes.string,
    size_mb: PropTypes.number,
    last_state: PropTypes.string,
    last_message: PropTypes.string,
    last_provider: PropTypes.string,
    can_update: PropTypes.bool,
  }),
  onUpdate: PropTypes.func.isRequired,
};

export default GeoDatabaseCard;
